import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material"
import {
  EvolutionStatChip,
  type IconUrl,
  formatPrice,
  isDefined,
  noDecimalsFormatOptions,
} from "@pharmupp/p3-front-commons"

const ICON_SIZE = 25
const PILLS_HEIGHT = 65

export interface SalesChipsProps {
  title: string
  Icon: IconUrl
  value?: number | null
  evolution?: number | null
  globalEvolution?: number | null
  onClick?: () => void
}

export const SalesChips = ({
  title,
  Icon,
  value,
  evolution,
  globalEvolution,
  onClick,
}: SalesChipsProps) => {
  const theme = useTheme()
  const linkProps = onClick ? { component: Button, onClick } : {}

  return (
    <Stack>
      <TitleContainer>
        <TitleTypography>{title}</TitleTypography>
        <GlobalEvolTitle>Evol. PharmUPP</GlobalEvolTitle>
      </TitleContainer>
      <SalesPills {...linkProps}>
        <SalesPillsContent>
          <IconContainer>
            <Icon size={ICON_SIZE} color={theme.palette.common.rareBlue} />
          </IconContainer>
          <Price>
            {isDefined(value) ? formatPrice(value, noDecimalsFormatOptions) : "-"}
          </Price>
        </SalesPillsContent>
        <SalesPillsContent>
          <EvolutionStatChip evol={evolution} />
          <EvolutionStatChip evol={globalEvolution} isNeutral />
        </SalesPillsContent>
      </SalesPills>
    </Stack>
  )
}

const TitleContainer = styled(Stack)(({ theme }) => ({
  justifyContent: "space-between",
  flexDirection: "row",
  alignItems: "center",
  paddingBottom: theme.spacing(0.5),
  paddingInline: theme.spacing(2),
}))

const TitleTypography = styled(Typography)({
  fontWeight: 500,
})
TitleTypography.defaultProps = {
  variant: "14px",
}
const GlobalEvolTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.grey.light,
}))
GlobalEvolTitle.defaultProps = {
  variant: "12px",
}
const SalesPills = styled(Stack)(({ theme, onClick }) => ({
  // Neutralize MuiButton padding/typography when rendered as a Button
  padding: theme.spacing(0, 2),
  lineHeight: theme.typography.body1.lineHeight,
  height: PILLS_HEIGHT,
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: theme.palette.common.americanDiamond,
  borderRadius: 200,
  color: "inherit",
  textDecoration: "none",
  ...(!!onClick && {
    "&:hover": {
      backgroundColor: theme.palette.common.ghostBlue,
    },
  }),
})) as typeof Stack
const SalesPillsContent = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  flexDirection: "row",
  alignItems: "center",
}))
const IconContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.common.rareBlue, 0.1),
  borderRadius: ICON_SIZE * 2,
}))
const Price = styled(Typography)({
  fontWeight: 600,
  alignSelf: "center",
})
Price.defaultProps = {
  variant: "16px",
}
